// DeskRows — the rows of an open binder or shelf (pen "Studio – the desk").
//  - A binder's documents, in reading order: number (filled once written,
//    hollow while planned), type icon, title (one line, full on hover), the
//    brief in one line when there is one, and the state.
//  - A shelf's modules as rows with a spine: a row unfolds in place to show
//    its documents (1.1, 1.2 …) or opens (lifting on top). A part that is not
//    a module (a course that does not fit its shape) is a document row.
// Every row's grip drags it to another place (usePartDrag.ts). Pure but for
// view-ephemeral state: which shelf rows are unfolded, a drag in progress.

export { BinderRows, partIcon, ShelfRows };

import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, type RefObject, useRef, useState } from "react";
import { Icon, type IconName } from "../Icon/Icon.js";
import type { PartIntent } from "../ModuleCard/partOps.js";
import {
    type PartDragHandlers,
    usePartDrag,
} from "../ModuleCard/usePartDrag.js";
import type { ContainerCard, ContainerPart } from "../Studio/studioCards.js";
import { type DocState, docStateOf } from "./deskFacts.js";

const ROW = ":scope > .bilda-desk__row";

const partIcon = (part: ContainerPart): IconName => {
    switch (part.kind) {
        case "source":
            return "source";
        case "material":
            return part.template === "quiz" || part.template === "question"
                ? "mcq"
                : "material";
        case "module":
            return "module";
        case "other":
            return "file";
        default:
            return exhaustSwitchCase(part.kind);
    }
};

const StateLabel = ({ state }: { readonly state: DocState }): ReactNode => {
    const { t } = useLingui();
    const [icon, text]: readonly [IconName, string] | readonly [null, null] =
        ((): readonly [IconName, string] | readonly [null, null] => {
            switch (state) {
                case "generated":
                    return ["check", t`Generated`];
                case "planned":
                    return ["dot", t`Planned`];
                case "writing":
                    return ["sparkle", t`Writing …`];
                case "empty":
                    return ["warning", t`Empty`];
                case "removed":
                    return ["error", t`Removed`];
                case "source":
                    return ["source", t`Source`];
                case "unknown":
                    return [null, null];
                default:
                    return exhaustSwitchCase(state);
            }
        })();
    return icon === null ? null : (
        <span className="bilda-desk__state" data-state={state}>
            <Icon name={icon} size="xs" />
            {text}
        </span>
    );
};

const Grip = ({
    title,
    at,
    drag,
}: {
    readonly title: string;
    readonly at: number;
    readonly drag: PartDragHandlers & { readonly id: string };
}): ReactNode => {
    const { t } = useLingui();
    return (
        <button
            type="button"
            className="bilda-desk__grip"
            aria-label={t`Drag to reorder ${title}`}
            tabIndex={-1}
            onPointerDown={drag.start(drag.id, at)}
            onPointerMove={drag.move}
            onPointerUp={drag.end}
            onPointerCancel={drag.cancel}
        >
            <Icon name="grip" size="xs" />
        </button>
    );
};

// The row's drag state for the canvas's CSS (dragged, where it would land).
const dragAttrs = (drag: PartDragHandlers, id: string, i: number) => ({
    "data-dragged": drag.drag?.part === id ? "true" : undefined,
    "data-landing": drag.landingAt(i),
});

const useRowDrag = (
    online: boolean,
    onPart: (intent: PartIntent) => void,
    list: RefObject<HTMLOListElement | null>,
) =>
    usePartDrag({
        list,
        rows: ROW,
        enabled: online,
        onMove: (part, from, to) => onPart({ type: "move", part, from, to }),
    });

// One document: open it with a click (or Enter).
const DocumentRow = ({
    part,
    number,
    onOpen,
}: {
    readonly part: ContainerPart;
    readonly number: string;
    readonly onOpen: () => void;
}): ReactNode => {
    const state = docStateOf(part);
    return (
        <button type="button" className="bilda-desk__doc" onClick={onOpen}>
            <span
                className="bilda-desk__number"
                // Hollow while nothing is written yet (planned, unknown).
                data-filled={
                    state === "planned" || state === "unknown"
                        ? undefined
                        : "true"
                }
            >
                {number}
            </span>
            <Icon name={partIcon(part)} size="sm" />
            <span className="bilda-desk__doc-text">
                <span className="bilda-desk__doc-title" title={part.title}>
                    {part.title}
                </span>
                {part.document?.brief === undefined ? null : (
                    <span
                        className="bilda-desk__brief"
                        title={part.document.brief}
                    >
                        {part.document.brief}
                    </span>
                )}
            </span>
            <StateLabel state={state} />
        </button>
    );
};

const BinderRows = ({
    card,
    online,
    onPart,
}: {
    readonly card: ContainerCard;
    readonly online: boolean;
    readonly onPart: (intent: PartIntent) => void;
}): ReactNode => {
    const list = useRef<HTMLOListElement>(null);
    const drag = useRowDrag(online, onPart, list);
    return (
        <ol ref={list} className="bilda-desk__rows">
            {card.parts.map((part, i) => (
                <li
                    key={part.id}
                    className="bilda-desk__row"
                    data-part-id={part.id}
                    {...dragAttrs(drag, part.id, i)}
                >
                    <Grip
                        title={part.title}
                        at={i}
                        drag={{ ...drag, id: part.id }}
                    />
                    <DocumentRow
                        part={part}
                        number={String(i + 1)}
                        onOpen={() => onPart({ type: "open", part: part.id })}
                    />
                </li>
            ))}
        </ol>
    );
};

const ShelfRows = ({
    card,
    online,
    onPart,
    onOpenModule,
}: {
    readonly card: ContainerCard;
    readonly online: boolean;
    readonly onPart: (intent: PartIntent) => void;
    readonly onOpenModule: (id: string) => void;
}): ReactNode => {
    const { t } = useLingui();
    const list = useRef<HTMLOListElement>(null);
    const drag = useRowDrag(online, onPart, list);
    const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(new Set());
    const toggle = (id: string) =>
        setUnfolded((s) =>
            s.has(id)
                ? new Set([...s].filter((x) => x !== id))
                : new Set([...s, id]),
        );
    return (
        <ol ref={list} className="bilda-desk__rows">
            {card.parts.map((part, i) => {
                const open = unfolded.has(part.id);
                const n = part.parts.length;
                return (
                    <li
                        key={part.id}
                        className="bilda-desk__row"
                        data-part-id={part.id}
                        data-unfolded={open ? "true" : undefined}
                        {...dragAttrs(drag, part.id, i)}
                    >
                        <Grip
                            title={part.title}
                            at={i}
                            drag={{ ...drag, id: part.id }}
                        />
                        {part.kind !== "module" ? (
                            <DocumentRow
                                part={part}
                                number={String(i + 1)}
                                onOpen={() =>
                                    onPart({ type: "open", part: part.id })
                                }
                            />
                        ) : (
                            <div className="bilda-desk__module">
                                <div className="bilda-desk__module-head">
                                    <button
                                        type="button"
                                        className="bilda-desk__unfold"
                                        aria-expanded={open}
                                        aria-label={
                                            open
                                                ? t`Hide the parts of ${part.title}`
                                                : t`Show the parts of ${part.title}`
                                        }
                                        onClick={() => toggle(part.id)}
                                    >
                                        <Icon
                                            name={
                                                open
                                                    ? "chevron-down"
                                                    : "chevron-right"
                                            }
                                            size="sm"
                                        />
                                    </button>
                                    <span
                                        className="bilda-desk__spine"
                                        aria-hidden="true"
                                    />
                                    <button
                                        type="button"
                                        className="bilda-desk__module-title"
                                        title={part.title}
                                        onClick={() => toggle(part.id)}
                                    >
                                        <span className="bilda-desk__module-number">
                                            {i + 1}
                                        </span>
                                        {part.title}
                                    </button>
                                    <span className="bilda-desk__count">
                                        {t`${plural(n, { one: "# document", other: "# documents" })}`}
                                    </span>
                                    <button
                                        type="button"
                                        className="bilda-desk__open"
                                        onClick={() => onOpenModule(part.id)}
                                    >
                                        {t`Open`}
                                    </button>
                                </div>
                                {open && n > 0 ? (
                                    <ol className="bilda-desk__subrows">
                                        {part.parts.map((sub, j) => (
                                            <li key={sub.id}>
                                                <button
                                                    type="button"
                                                    className="bilda-desk__subrow"
                                                    data-part-id={sub.id}
                                                    data-planned={
                                                        sub.planned
                                                            ? "true"
                                                            : undefined
                                                    }
                                                    title={sub.title}
                                                    onClick={() =>
                                                        onPart({
                                                            type: "open",
                                                            part: sub.id,
                                                        })
                                                    }
                                                >
                                                    <span className="bilda-desk__subnumber">
                                                        {`${i + 1}.${j + 1}`}
                                                    </span>
                                                    <span className="bilda-desk__subtitle">
                                                        {sub.planned
                                                            ? t`${sub.title} (planned)`
                                                            : sub.title}
                                                    </span>
                                                </button>
                                            </li>
                                        ))}
                                    </ol>
                                ) : null}
                            </div>
                        )}
                    </li>
                );
            })}
        </ol>
    );
};
